/**
 * @file     buyingCosts.ts
 * @purpose  Closing-cost breakdown for a BC purchase, worked against a city's
 *           real median asking price. The buy-side mirror of sellingCosts.
 *           Statutory rates are verified against gov.bc.ca and dated, because
 *           a wrong tax figure on a public page is a real harm, not a typo.
 *           Deterministic (ADR-009).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { listMarketReportCities } from '@/services/mls/marketReport';
import { neighbourhoodSlug } from '@/services/mls/neighbourhoods';
import { reportSilentFailure } from '@/core/observability/reportSilentFailure';
import { formatCadExact as money } from '@/utils/dataPresentation';
import { minimumDownPayment } from '@/core/finance/downPayment';
import { CLOSING_COST_RANGES } from '@/core/costs/closingCosts';

export { minimumDownPayment } from '@/core/finance/downPayment';

/**
 * Same floor the cost-of-selling family uses. A city needs a stable median
 * before a cost page about it means anything, and publishing every hamlet is
 * the doorway pattern.
 */
export const MIN_COST_PAGE_LISTINGS = 150;

/**
 * Statutory figures, verified 2026-07-22 against gov.bc.ca. Reviewed whenever
 * the province changes them; every page states this date.
 */
export const RATES_VERIFIED_ON = '2026-07-22';

// Transfer-tax and first-time math live in a client-safe module so the map
// estimator and this service compute from one source. Re-exported to keep this
// module's public API and its tests unchanged.
export {
  propertyTransferTax,
  firstTimeBuyerRelief,
  FTHB_EXEMPT_PORTION,
  FTHB_PARTIAL_FROM,
  FTHB_PARTIAL_TO,
} from '@/core/tax/transferTax';
import { propertyTransferTax, firstTimeBuyerRelief, FTHB_PARTIAL_FROM, FTHB_PARTIAL_TO } from '@/core/tax/transferTax';

/** Illustrative third-party costs, clearly labelled as estimates everywhere. */
export const LEGAL_AND_CONVEYANCING = CLOSING_COST_RANGES.legal.typical;
export const HOME_INSPECTION = CLOSING_COST_RANGES.inspection.typical;
export const APPRAISAL = CLOSING_COST_RANGES.appraisal.typical;
export const TITLE_INSURANCE = CLOSING_COST_RANGES.titleInsurance.typical;

export interface BuyingCostBreakdown {
  city: string;
  slug: string;
  activeCount: number;
  medianPrice: number;
  transferTax: number;
  /** Tax removed by the first-time buyer exemption, when it applies. */
  firstTimeRelief: number;
  transferTaxFirstTime: number;
  legal: number;
  inspection: number;
  appraisal: number;
  titleInsurance: number;
  /** Everything payable on closing, excluding the down payment. */
  closingTotal: number;
  closingTotalFirstTime: number;
  minimumDown: number;
  /** Cash needed on day one: minimum down plus closing costs. */
  cashNeeded: number;
  cashNeededFirstTime: number;
  pctOfPrice: number;
}

export function costsForPrice(
  city: string,
  slug: string,
  activeCount: number,
  medianPrice: number,
): BuyingCostBreakdown {
  const transferTax = propertyTransferTax(medianPrice);
  const firstTimeRelief = firstTimeBuyerRelief(medianPrice);
  const thirdParty = LEGAL_AND_CONVEYANCING + HOME_INSPECTION + APPRAISAL + TITLE_INSURANCE;
  const closingTotal = transferTax + thirdParty;
  const closingTotalFirstTime = transferTax - firstTimeRelief + thirdParty;
  const minimumDown = minimumDownPayment(medianPrice);
  return {
    city,
    slug,
    activeCount,
    medianPrice,
    transferTax,
    firstTimeRelief,
    transferTaxFirstTime: transferTax - firstTimeRelief,
    legal: LEGAL_AND_CONVEYANCING,
    inspection: HOME_INSPECTION,
    appraisal: APPRAISAL,
    titleInsurance: TITLE_INSURANCE,
    closingTotal,
    closingTotalFirstTime,
    minimumDown,
    cashNeeded: minimumDown + closingTotal,
    cashNeededFirstTime: minimumDown + closingTotalFirstTime,
    pctOfPrice: Math.round((closingTotal / medianPrice) * 1000) / 10,
  };
}

/**
 * Cities big enough to carry a cost page. Mirrors listSellingCostCities so the
 * buy and sell families cover exactly the same places.
 */
export async function listBuyingCostCities(
  db: SupabaseClient,
): Promise<BuyingCostBreakdown[]> {
  const cities = await listMarketReportCities(db, MIN_COST_PAGE_LISTINGS).catch((err: unknown) => {
    reportSilentFailure(err, { area: 'buyer/cost-cities' });
    return [] as {
      city: string;
      count: number;
      medianPrice: number | null;
      lastModified: string | null;
    }[];
  });

  const out: BuyingCostBreakdown[] = [];
  for (const c of cities) {
    const median = c.medianPrice;
    if (median == null || median <= 0) continue;
    out.push(costsForPrice(c.city, neighbourhoodSlug(c.city), c.count, median));
  }
  return out;
}

/**
 * Plain-language read built only from figures shown on the page. Never states
 * the first-time exemption as a given: it carries residency and ownership
 * conditions this page cannot check for the reader.
 */
export function costNarrative(b: BuyingCostBreakdown, bcMedianPct: number | null): string[] {
  const out: string[] = [];

  out.push(
    `The median asking price in ${b.city} is ${money(b.medianPrice)}. On a purchase at that price the closing costs come to roughly ${money(b.closingTotal)}, about ${b.pctOfPrice}% of the price, on top of your down payment.`,
  );

  out.push(
    `Property transfer tax is the largest single piece at ${money(b.transferTax)}. The rest is legal and conveyancing, an inspection, an appraisal and title insurance, which together run about ${money(b.closingTotal - b.transferTax)}.`,
  );

  if (b.firstTimeRelief > 0) {
    out.push(
      `A qualifying first-time buyer would pay ${money(b.transferTaxFirstTime)} of that transfer tax instead of ${money(b.transferTax)}, bringing closing costs to about ${money(b.closingTotalFirstTime)}. The exemption carries residency and prior-ownership conditions, so treat it as a figure to confirm rather than assume.`,
    );
  } else if (b.medianPrice >= FTHB_PARTIAL_TO) {
    out.push(
      `At this price the first-time buyer exemption does not apply. It phases out between ${money(FTHB_PARTIAL_FROM)} and ${money(FTHB_PARTIAL_TO)} and is gone above that, so in ${b.city} it reaches only the lower end of the market.`,
    );
  }

  out.push(
    `The minimum down payment on ${money(b.medianPrice)} is ${money(b.minimumDown)}, which puts the cash needed to close at roughly ${money(b.cashNeeded)}${b.firstTimeRelief > 0 ? `, or ${money(b.cashNeededFirstTime)} as a first-time buyer` : ''}.`,
  );

  if (bcMedianPct != null) {
    const diff = Math.round((b.pctOfPrice - bcMedianPct) * 10) / 10;
    out.push(
      Math.abs(diff) < 0.2
        ? `As a share of price, that is in line with the rest of the province, where the median is about ${bcMedianPct}%.`
        : `As a share of price that is ${Math.abs(diff)} points ${diff > 0 ? 'above' : 'below'} the provincial median of about ${bcMedianPct}%, which is mostly a function of price level: the transfer tax is charged on a rising scale.`,
    );
  }

  return out;
}
